/**
 * SourceDrawer — Slide-out panel showing full source details for a citation.
 *
 * Displays: source kind icon, dataset name, table, row count, query duration,
 * data freshness badge, and a "View in BigQuery" deep link.
 */

import React from 'react';
import { C } from '../design-system/primitives/constants';
import FreshnessBadge from './FreshnessBadge';

const KIND_CONFIG = {
  bigquery: { icon: '🗄️', label: 'BigQuery', color: C.gen, bgColor: `${C.gen}15` },
  natstat: { icon: '📊', label: 'NatStat', color: C.elite, bgColor: `${C.elite}15` },
  web: { icon: '🌐', label: 'Web', color: '#a855f7', bgColor: '#9333ea15' },
  vertex_search: { icon: '🔎', label: 'SLAM KB', color: '#22d3ee', bgColor: '#06b6d415' },
  system: { icon: '⚙️', label: 'System', color: C.t2, bgColor: `${C.t3}15` },
};

function SourceCard({ citation }) {
  const config = KIND_CONFIG[citation.kind] || KIND_CONFIG.system;

  return (
    <div
      className="rounded-[14px] p-3 mb-2 transition-all duration-200"
      style={{ background: C.s1, border: `1px solid ${C.border}` }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <span
          className="flex items-center justify-center w-7 h-7 rounded-lg text-sm"
          style={{ background: config.bgColor }}
        >
          {config.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold" style={{ color: config.color }}>
            [{citation.ref}] {citation.label}
          </div>
          <div className="text-[10px]" style={{ color: C.t3 }}>
            {config.label}
          </div>
        </div>
        {citation.freshness && (
          <FreshnessBadge date={citation.freshness} compact />
        )}
      </div>

      {/* Details */}
      <div className="space-y-1.5">
        {citation.dataset && (
          <div className="flex items-center gap-2 text-[11px]" style={{ color: C.t2 }}>
            <span className="w-14 flex-shrink-0 font-medium">Dataset</span>
            <code className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: C.s2, color: C.text }}>
              {citation.dataset}
            </code>
          </div>
        )}
        {citation.tables && citation.tables.length > 0 && (
          <div className="flex items-center gap-2 text-[11px]" style={{ color: C.t2 }}>
            <span className="w-14 flex-shrink-0 font-medium">Tables</span>
            <span className="flex flex-wrap gap-1">
              {citation.tables.map((t, i) => (
                <code key={i} className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: C.s2, color: C.text }}>
                  {t}
                </code>
              ))}
            </span>
          </div>
        )}
        {citation.rows != null && (
          <div className="flex items-center gap-2 text-[11px]" style={{ color: C.t2 }}>
            <span className="w-14 flex-shrink-0 font-medium">Rows</span>
            <span>{citation.rows.toLocaleString()}</span>
          </div>
        )}
        {citation.duration_ms != null && (
          <div className="flex items-center gap-2 text-[11px]" style={{ color: C.t2 }}>
            <span className="w-14 flex-shrink-0 font-medium">Latency</span>
            <span>{citation.duration_ms < 1000 ? `${citation.duration_ms}ms` : `${(citation.duration_ms / 1000).toFixed(1)}s`}</span>
          </div>
        )}
        {citation.domain && (
          <div className="flex items-center gap-2 text-[11px]" style={{ color: C.t2 }}>
            <span className="w-14 flex-shrink-0 font-medium">Domain</span>
            <span>{citation.domain}</span>
          </div>
        )}
      </div>

      {/* Deep link */}
      {citation.uri && /^https?:\/\//.test(citation.uri) && (
        <a
          href={citation.uri}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 mt-2.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all"
          style={{ background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold }}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          {citation.kind === 'web' ? 'Open Source' : 'View in BigQuery'}
        </a>
      )}
    </div>
  );
}

export function SourceDrawer({ citations, isOpen, onClose }) {
  if (!isOpen || !citations?.length) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div
        className="fixed right-0 top-0 bottom-0 z-50 w-80 max-w-[90vw] overflow-y-auto transition-transform duration-300 ease-out backdrop-blur-xl"
        style={{ background: `${C.s1}F2`, borderLeft: `1px solid ${C.border}`, animation: 'slide-in-right 0.25s ease-out' }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between p-4 backdrop-blur-xl"
          style={{ background: `${C.s1}F2`, borderBottom: `1px solid ${C.border}` }}
        >
          <div>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
              Sources
            </h3>
            <p style={{ fontSize: 11, marginTop: 2, color: C.t3 }}>
              {citations.length} data source{citations.length !== 1 ? 's' : ''} used
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-all hover:bg-[rgba(255,255,255,0.04)]"
            style={{ color: C.t2 }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Citation Cards */}
        <div className="p-3">
          {citations.map((c) => (
            <SourceCard key={c.ref} citation={c} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}

export default SourceDrawer;
